import { useState, useEffect, FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { AuthGetTokens } from "@/legacy/ApiAuth";
import { AuthGetLoggedinAgent } from "@/legacy/ApiCallerOld";
import { agentLoggedIn } from "../../Store/Reducers/loggedInAgentSlice";
import { authUpdated } from "../../Store/Reducers/authSlice";
import { useAppDispatch } from "../../Store/hooks";
import { DeviceFunctions } from "../../Helpers/DeviceFunctions";
import { AlertBox } from "@shared/Components/AlertBox/AlertBox";
import { AlertType } from "@shared/Components/AlertBox/AlertType";
import Modal from "@shared/Components/Modal/Modal";
import ModalContent from "@shared/Components/Modal/ModalContent";
import ModalButtons from "@shared/Components/Modal/ModalButtons";

export default function Login() {
	document.title = "Login | GoDesk";

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const [reqInProgress, setReqInProgress] = useState(false);
	const [alertType, setAlertType] = useState<AlertType | undefined>();
	const [alertText, setAlertText] = useState("");
	const [isInvalidInstance, setIsInvalidInstance] = useState(false);
	const [mobWarningModalOpen, setMobWarningModalOpen] = useState(DeviceFunctions.detectMob);

	const searchParams = new URLSearchParams(search);

	// If we've just been logged out, show a msg.
	const didLogout = searchParams.get("loggedout") != null;

	// If we have successfully added a Cname.
	const cnameAdded = searchParams.get("cnamerequest") != null;

	useEffect(() => {
		// Check if we're already logged in. Function will redirect to home if so.
		async function checkApiLoggedInAgent() {
			const res = await AuthGetLoggedinAgent();

			if (res.successful) {
				if (res.data?.id != null) {
					dispatch(agentLoggedIn(res.data));
					doLoginRedirect();
				}
			} else if (res.errorCode == 404) { // Instance does not exist.
				setIsInvalidInstance(true);
			} else {
				console.log("Could not do checkApiLoggedInAgent(). Error in auth: " + res.errorCode + ": " + res.errorMsg);
			}
		}

		checkApiLoggedInAgent();
	}, []);

	async function handleSubmit(event: FormEvent) {
		event.preventDefault();

		setReqInProgress(true);
		setAlertType(undefined);
		setAlertText("");

		const username = (document.getElementById("usernameInput") as HTMLInputElement)?.value ?? "";
		const password = (document.getElementById("passwordInput") as HTMLInputElement)?.value ?? "";

		const tokenRes = await AuthGetTokens(username, password);

		if (!tokenRes.successful) {
			setAlertType("error");
			setAlertText("Error in auth: " + tokenRes.errorMsg);
		}

		setReqInProgress(false);

		if (tokenRes.data != null) {
			dispatch(authUpdated({ accessToken: tokenRes.data.accessToken, refreshToken: tokenRes.data.refreshToken }));

			const loggedInAgentRes = await AuthGetLoggedinAgent();

			if (loggedInAgentRes.successful) {
				if (loggedInAgentRes.data?.id != null) {
					dispatch(agentLoggedIn(loggedInAgentRes.data));
					doLoginRedirect();
				} else {
					setAlertType("error");
					setAlertText("Error in auth: Could not log you in.");
				}
			} else {
				setAlertType("error");
				setAlertText("Error in auth: " + loggedInAgentRes.errorMsg);
			}
		} else if (tokenRes.errorCode == 401) {
			setAlertType("warning");
			setAlertText("Incorrect Credentials.");
		} else if (tokenRes.errorCode == 404) {
			setAlertType("error");
			setAlertText("This instance of GoDesk does not exist. Are you on the correct URL?");
		} else {
			setAlertType("error");
			setAlertText("Unknown auth error.");
		}
	}

	function doLoginRedirect() {
		const searchParams = new URLSearchParams(search);
		const redirectParam = searchParams.get("redirect");

		if (redirectParam != null && redirectParam != "") {
			searchParams.delete("redirect");
			navigate({ pathname: redirectParam, search: searchParams.toString() });
		} else {
			navigate("/");
		}
	}

	if (isInvalidInstance) {
		return (
			<div className="h-screen flex items-center justify-center" style={{ backgroundColor: "#E1F2FE" }}>
				<div className="max-w-md w-full mx-auto bg-gray-50 py-8 px-5 rounded-md shadow-md md:-translate-y-1/3">
					<div className="w-2/3 mx-auto mb-6">
						<img src="/logowithtext.png" alt="GoDesk Logo" />
					</div>

					<AlertBox alertType={"error"}>
						This instance of GoDesk does not exist. Are you on the correct URL?
					</AlertBox>
				</div>
			</div>
		);
	}

	return (
		<div className="h-screen flex items-center justify-center" style={{ backgroundColor: "#E1F2FE" }}>
			<div className="max-w-md w-full mx-auto bg-gray-50 py-8 px-5 rounded-md shadow-md md:-translate-y-1/3">
				<div className="w-2/3 mx-auto mb-6">
					<img src="/logowithtext.png" alt="GoDesk Logo" />
				</div>

				{alertType != null ?
					<AlertBox alertType={alertType}>
						{alertText}
					</AlertBox>
					: null}

				{didLogout &&
					<AlertBox alertType="info">
						You have been logged out.
					</AlertBox>}

				{cnameAdded &&
					<AlertBox alertType="info">
						CNAME added successfully.
					</AlertBox>}

				<form className="" onSubmit={handleSubmit}>
					<label htmlFor="usernameInput">Work Email</label>
					<input
						id="usernameInput"
						type="text"
						placeholder="me@company.com"
						disabled={reqInProgress}
						autoComplete="username"
						className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500 mb-4"
					/>

					<label htmlFor="passwordInput">Password</label>
					<input
						id="passwordInput"
						type="password"
						placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
						disabled={reqInProgress}
						autoComplete="current-password"
						className="mt-1 block w-full shadow-sm border-gray-300 rounded-md py-2 px-3 border focus:border-blue-500"
					/>

					<div className="flex items-center">
						<div className="h-10" />
						<Link to={"/password-reset"} className="ml-auto text-blue-600 underline">Forgot password?</Link>
					</div>

					<div className="w-full flex flex-row justify-center">
						<input className="btn-blue mt-4 cursor-pointer" type="submit" value="Login" disabled={reqInProgress} />
					</div>
				</form>
			</div>

			<Modal
				isOpen={mobWarningModalOpen}
				onClose={() => setMobWarningModalOpen(false)}
				title="Mobile layout"
			>
				<ModalContent>
					<h2 className="text-lg">
						Unfortunately, GoDesk does not currently have a mobile layout. We recommend using GoDesk on desktops/laptops for now.
						<br /><br />
						Our mobile version will be delivered later this year.
					</h2>
				</ModalContent>

				<ModalButtons>
					<button className="btn-blue" onClick={() => setMobWarningModalOpen(false)}>Ok</button>
				</ModalButtons>
			</Modal>
		</div>

	);
}
